import React, { useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import geoViewport from "@mapbox/geo-viewport";
import { transformDataToMapboxSource } from "utils";
// eslint-disable-next-line
import mapboxgl from "!mapbox-gl";

mapboxgl.accessToken = process.env.REACT_APP_MAPGL_ACCESS_TOKEN;

const Map = ({
  containerStyle,
  mapboxStyle,
  data,
  handleSelectLayer,
  selectedLayers,
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);

  const initializeFields = useCallback(() => {
    if (data?.fields.length === 0) return;

    const transformedFields = transformDataToMapboxSource(data.fields);

    transformedFields.forEach((item) => {
      const { id } = item.data;
      map.current.addSource(`source-${id}`, item);
      map.current.addLayer({
        id: `outline-${id}`,
        type: "line",
        source: `source-${id}`,
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#fff",
          "line-width": 2,
        },
      });
      map.current.addLayer({
        id: `fill-${id}`,
        type: "fill",
        source: `source-${id}`,
        paint: {
          "fill-color": "#fff",
          "fill-opacity": 0,
        },
      });
      map.current.on("click", `fill-${id}`, () => {
        handleSelectLayer(item.data);
      });
    });
  }, [data.fields, handleSelectLayer]);

  useEffect(() => {
    if (map.current) return;

    const mapEl = document.getElementById("mapbox").getBoundingClientRect();
    const mapDim = [mapEl.height, mapEl.width];
    const newbounds = geoViewport.viewport(data.bbox, mapDim, 0, 20, 512);

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapboxStyle,
      center: newbounds.center,
      zoom: newbounds.zoom,
    });

    map.current.on("load", () => {
      if (!data) return;

      initializeFields();
    });
  }, [data, initializeFields, mapboxStyle]);

  useEffect(() => {
    if (!map.current.isStyleLoaded()) {
      return;
    }

    data.fields.map((item) => {
      const layer = map.current.getLayer(`outline-${item.id}`);

      if (layer) {
        map.current.setPaintProperty(
          `outline-${item.id}`,
          "line-color",
          "#fff"
        );
      }

      return null;
    });

    if (selectedLayers.length > 0) {
      selectedLayers.map((item) => {
        const layer = map.current.getLayer(`outline-${item.id}`);

        if (layer) {
          map.current.setPaintProperty(
            `outline-${item.id}`,
            "line-color",
            "#09BA51"
          );
        }

        return null;
      });
    }
  }, [data.fields, selectedLayers]);

  return (
    <div>
      <div
        ref={mapContainer}
        className="map-container"
        style={containerStyle}
        id="mapbox"
      />
    </div>
  );
};

Map.defaultProps = {
  containerStyle: { height: "400px" },
  mapboxStyle: "mapbox://styles/mapbox/satellite-v9",
  data: null,
  handleSelectLayer: () => {},
  selectedLayers: [],
};

Map.propTypes = {
  containerStyle: PropTypes.objectOf(PropTypes.any),
  mapboxStyle: PropTypes.string,
  data: PropTypes.objectOf(PropTypes.any),
  handleSelectLayer: PropTypes.func,
  selectedLayers: PropTypes.arrayOf(PropTypes.any),
};

export default Map;
